import { addPlaybackServiceWorkerListener } from './listener';
import { freezeAttributes, freezeDataProperty, freezeDataProperties } from '@fullstory/util/freeze';
import { createRelativeURL } from '@fullstory/util/url';
import { normalizeDoctype } from '@fullstory/playback-service-worker';
import { PlaybackContext, urlWithPlaybackInstance } from '@fullstory/renderer-shared';
import { RestrictedAttrValues, UnrestrictedAttr } from '@fullstory/rewriters';
import { shorten } from '@fullstory/util/fmt';

/** APIs exposed on controlled <frame> and <iframe> elements. */
export interface IControlledFrame {
  /** Resolves when the frame is loaded and ready. */
  readonly loaded: Promise<void>;
}

export interface ControlledIFrameElement extends HTMLIFrameElement, IControlledFrame {
  // Existing methods that are restricted for controlled iframes.
  setAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>, value: string): void;
  setAttributeNS<Attr extends string>(
    ns: string | null,
    name: UnrestrictedAttr<Attr>,
    value: string,
  ): void;
  toggleAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>, force?: boolean): boolean;
  removeAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>): void;
  removeAttributeNS<Attr extends string>(ns: string | null, name: UnrestrictedAttr<Attr>): void;

  // Existing properties that are readonly for controlled iframes.
  readonly csp: string;
  readonly src: string;
  readonly srcdoc: string;
}

export interface ControlledFrameElement extends HTMLFrameElement, IControlledFrame {
  // Existing methods that are restricted for controlled frames.
  setAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>, value: string): void;
  setAttributeNS<Attr extends string>(
    ns: string | null,
    name: UnrestrictedAttr<Attr>,
    value: string,
  ): void;
  toggleAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>, force?: boolean): boolean;
  removeAttribute<Attr extends string>(name: UnrestrictedAttr<Attr>): void;
  removeAttributeNS<Attr extends string>(ns: string | null, name: UnrestrictedAttr<Attr>): void;

  // Existing properties that are readonly for controlled frames.
  readonly src: string;
}

export type ControlledFramelikeElement = ControlledFrameElement | ControlledIFrameElement;

export interface ControlledFrameElementOfKind {
  frame: ControlledFrameElement;
  iframe: ControlledIFrameElement;
}
export type ControlledFrameKind = keyof ControlledFrameElementOfKind;

// Browsers may generate a spurious load event for the initial 'about:blank'
// document in a frame before navigating to the final document. It's unclear
// whether this is required by the spec, but whether this is a bug or not, we
// want to ignore it.
function isSpuriousLoadEvent(
  frameHref: string,
  loadedDoc: Document,
  expectedDoctype: string,
  gotAtLeastOneRealLoadEvent: boolean,
): boolean {
  // Frame href's can be VERY long.
  const shortHref = shorten(frameHref);
  // If the URLs match and we have a doctype, treat this as the real load event.
  // The latter condition helps differentiate the real load event from the
  // spurious one on Firefox, where the initial document sometimes has the same
  // URL as the enclosing document, but otherwise behaves like an empty
  // 'about:blank' document with no doctype.
  if (
    frameHref === loadedDoc.URL &&
    (frameHref === 'about:blank' || loadedDoc.doctype || expectedDoctype === '')
  ) {
    if (gotAtLeastOneRealLoadEvent) {
      console.warn(
        `Got a duplicate load event (which will almost certainly ` +
          `cause problems) for '${shortHref}'`,
      );
    } else {
      console.debug(`Got the load event for '${shortHref}'`);
    }
    return false;
  }

  const shortDocUrl = shorten(loadedDoc.URL);
  // Don't treat this as spurious if we've already gotten a real load event.
  // This almost certainly indicates a bug; we're doing something to cause the
  // frame to reload. (Despite the many protections that are in place to
  // prevent that!)
  if (gotAtLeastOneRealLoadEvent) {
    console.warn(`Got a load event for ${shortDocUrl}, but we expected to load ${shortHref}`);
    return false;
  }

  console.debug(
    `Got an initial spurious load event for controlled frame ` +
      `'${shortHref}'; still waiting for the real load event. ${shortDocUrl}`,
  );
  return true;
}

let nextFrameId = 0;

type CreateFrameURLOpts = Pick<ControlledFrameOptions, 'href' | 'baseUrl' | 'ctx'> & {
  doctype: string;
};

function createFrameURL(opts: CreateFrameURLOpts): string {
  const { baseUrl, ctx, doctype, href } = opts;
  // Create an absolute URL, relative to the current document URL,
  // and add the playback context/instance.
  //
  // If the PlaybackContext caching flag is enabled, explicitly drop the hash
  // because it includes the full PlaybackContext and costly resource map.
  // When the flag is enabled, the service worker will use the
  // PlaybackInstance to resolve the correct PlaybackContext.
  //
  // Explicitly preserve the query string, which could include useful values.
  // However, note that the PlaybackInstance will be added regardless of
  // whether or not the query string is preserved.
  const frameURL = ctx.flags.features.AppPlaybackPlaybackContextCaching
    ? urlWithPlaybackInstance(
        createRelativeURL(location.href, href, {
          preserveHash: false,
          preserveQuery: true,
          preserveOrigin: true,
        }),
        ctx,
      )
    : createRelativeURL(location.href, href, {
        preserveHash: true,
        preserveQuery: true,
        preserveOrigin: true,
      });

  // Request that the playback service worker generate a document with the
  // provided doctype.
  frameURL.searchParams.set('doctype', doctype);
  frameURL.searchParams.set('baseurl', baseUrl);

  // Use an incrementing id to ensure that no controlled frame is ever nested
  // inside another frame with the same URL. This works around a bug that
  // occurs in both Chrome and Firefox which causes the load event to never fire
  // when a nested frame has either no 'src' or the same 'src' as the enclosing
  // frame. See here for the Firefox bug:
  //  https://bugzilla.mozilla.org/show_bug.cgi?id=1229707
  frameURL.searchParams.set('id', nextFrameId.toString());
  nextFrameId++;

  return frameURL.href;
}

/**
 * Create a <frame> or <iframe> element which will be controlled by the playback
 * service worker. Any frame used to contain replay content should be created
 * this by function.
 *
 * NOTE: We officially do not support <frame> and <frameset>, which are obsolete
 * elements and not part of HTML5. This code is a minimal, best-effort attempt
 * to support customers who use these features, but perfection is explicitly not
 * a goal.
 *
 * @param doc - The document in which the frame element will live.
 * @param doctype - The doctype that the frame's document should have.
 * @param features - The playback features that should be enabled for the replay
 * content inside the frame.
 * @param kind - The kind of frame to create.
 * @param baseUrl - base url for the page
 */
export function createControlledFrame<Kind extends ControlledFrameKind>(
  ctx: PlaybackContext,
  doc: Document,
  doctype: string,
  kind: Kind,
  baseUrl: string = 'about:blank',
): ControlledFrameElementOfKind[Kind] {
  return createControlledFrameOfKind(kind, {
    ctx,
    href: 'iframe.html',
    doc,
    requestedDoctype: doctype,
    baseUrl,
  });
}

function cypressIsPreventingSpuriousLoadEventDetection(frameHref: string): boolean {
  try {
    if (!('Cypress' in window.parent)) {
      return false;
    }
  } catch (e) {
    // Accessing 'window.parent' failed, almost certainly because it's
    // cross-origin. Swallow the exception and assume we're not running on
    // Cypress; this never happens there, because Cypress disables most security
    // features.
    return false;
  }

  // Cypress injects a script into all documents *before* the <!DOCTYPE> node,
  // which prevents the <!DOCTYPE> node from having any effect and leads us to
  // treat the load event as spurious. Just treat all load events as real if
  // we're running in Cypress.
  // TODO(sethf): There's currently no way to disable this at the Cypress level,
  // but once there is, we should remove this.
  console.debug(
    `Got the load event for '${shorten(frameHref)}'; treating it as ` +
      `real unconditionally since we're running on Cypress`,
  );
  return true;
}

// Cypress automatically injects content into all HTML documents. Leaving it
// there will cause playback to break, because the DOM will contain unexpected
// nodes, so we need to remove it.
function removeContentInjectedByCypress(frameDoc: Document) {
  const nodesToRemove: Node[] = [];
  for (let node: Node | null = frameDoc.firstChild; node; node = node.nextSibling) {
    if ('_fs' in node) {
      continue; // Don't remove anything created by the playback script.
    }
    if (node.nodeType != Node.DOCUMENT_TYPE_NODE) {
      nodesToRemove.push(node); // The frame should only contain <!DOCTYPE>.
    }
  }
  for (const node of nodesToRemove) {
    console.debug(`Removing Cypress-injected node from frame: ${node.nodeName}`);
    frameDoc.removeChild(node);
  }
}

type ControlledFrameOptions = {
  ctx: PlaybackContext;
  href: string;
  doc: Document;
  requestedDoctype: string;
  baseUrl: string;
};

function createControlledFrameOfKind<Kind extends ControlledFrameKind>(
  kind: Kind,
  { ctx, href, doc, requestedDoctype, baseUrl }: ControlledFrameOptions,
): ControlledFrameElementOfKind[Kind] {
  // Normalize the provided doctype using the same algorithm that the service
  // worker uses. On the non-service-worker code path, this protects against
  // code injection via the doctype. On the service worker code path, this makes
  // spurious load event detection easier since we know what doctype we expect
  // the document to start off with.
  const doctype = normalizeDoctype(requestedDoctype, baseUrl);

  const frameHref = createFrameURL({ href, doctype, baseUrl, ctx });

  // Create a frame of the requested type. We'll freeze all properties that
  // apply to either type below; freezing properties that aren't present on the
  // original object doesn't do any harm.
  const frame = doc.createElement(kind) as ControlledFrameElement & ControlledIFrameElement;

  let gotAtLeastOneRealLoadEvent = false;
  const loaded = new Promise<void>(resolve => {
    frame.addEventListener('load', () => {
      const loadedDoc = frame.contentDocument;
      if (!loadedDoc) {
        console.error(`Got the 'load' event, but contentDocument is null?`);
        return;
      }

      if (cypressIsPreventingSpuriousLoadEventDetection(frameHref)) {
        removeContentInjectedByCypress(loadedDoc);
      } else if (isSpuriousLoadEvent(frameHref, loadedDoc, doctype, gotAtLeastOneRealLoadEvent)) {
        return;
      }
      gotAtLeastOneRealLoadEvent = true;

      addPlaybackServiceWorkerListener(ctx, frame.contentWindow);

      resolve();
    });
    frame.addEventListener('error', () => {
      console.error(`Got error event for frame ${shorten(frameHref)}`);
    });
  });

  // Don't allow restricted attributes.
  freezeAttributes(frame, RestrictedAttrValues);

  // Do the same for properties.
  // TODO(sethf): It'd be nice to make use of the 'csp' property, but as of
  // 10/2018, nobody but Blink supports it.
  freezeDataProperties(frame, ['csp', 'src', 'srcdoc']);

  // Set up the IControlledFrame API.
  freezeDataProperty(frame, 'loaded', loaded);

  // Use a sandbox to disable side effects. ('allow-same-origin' is
  // still necessary so we can touch the DOM of the nested iframe). This is
  // defense-in-depth; our CSP policies also block scripts and apply sandboxing.
  // note(eugenechang) allow-scripts is required for canvas to operate
  //  for more information, see https://html.spec.whatwg.org/multipage/canvas.html#the-canvas-element
  //  specifically, when scripting is disabled, canvas element serves fallback content
  const entitlements: string[] = ['allow-same-origin', 'allow-scripts'];

  Element.prototype.setAttribute.call(frame, 'sandbox', entitlements.join(' '));
  Element.prototype.setAttribute.call(frame, 'src', frameHref);
  return frame;
}
